import React from "react";
import { MessageCircle, Mail, Instagram } from "lucide-react";
import { useTranslation } from "react-i18next";
import logo from "@/assets/logo-sm.png";
import { BRAND, whatsappUrl, mailtoUrl } from "@/lib/brand";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export const Footer = () => {
  const { t } = useTranslation();

  const NAV = [
    { key: "home", h: "#inicio" },
    { key: "collections", h: "#colecoes" },
    { key: "about", h: "#sobre" },
    { key: "contact", h: "#contato" },
  ];

  const talkMessage = t("common.defaultWhatsappMessage");
  const emailSubject = t("common.defaultEmailSubject");

  return (
    <footer
      data-testid="footer"
      className="bg-[#1A1A1A] text-white/80 pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid md:grid-cols-3 gap-10 pb-12 border-b border-white/10">
          <div>
            <div className="flex items-start mb-5">
              <img
                src={logo}
                alt={t("nav.logoAlt")}
                className="h-7 w-auto brightness-0 invert"
              />
              <sup
                aria-label={t("nav.trademarkAria")}
                className="ml-0.5 mt-0.5 text-[9px] font-body font-medium text-white/50 leading-none select-none"
              >
                ®
              </sup>
            </div>
            <p className="font-body text-sm text-white/60 max-w-xs">
              {t("footer.brandDescription")}
            </p>
            <div className="mt-6">
              <LanguageSwitcher variant="header-dark" />
            </div>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">
              {t("footer.navTitle")}
            </h4>
            <ul className="space-y-2 font-body text-sm">
              {NAV.map((i) => (
                <li key={i.h}>
                  <a
                    href={i.h}
                    className="text-white/60 hover:text-white transition-colors"
                  >
                    {t(`nav.${i.key}`)}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display font-bold text-white mb-4">
              {t("footer.contactTitle")}
            </h4>
            <ul className="space-y-3 font-body text-sm">
              <li>
                <a
                  href={whatsappUrl(talkMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                >
                  <MessageCircle size={16} />
                  {BRAND.phoneDisplay}
                </a>
              </li>

              <li>
                <a
                  href={mailtoUrl(emailSubject)}
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                >
                  <Mail size={16} />
                  {BRAND.email}
                </a>
              </li>

              <li>
                <a
                  href={BRAND.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
                >
                  <Instagram size={16} />@{BRAND.instagram}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="font-body text-xs text-white/40 text-center md:text-left space-y-1">
            <p data-testid="footer-copyright">
              {t("footer.copyright", {
                year: new Date().getFullYear(),
                brand: BRAND.name,
              })}
            </p>
            <p data-testid="footer-company-info">{t("footer.companyInfo")}</p>
          </div>

          <p className="font-body text-xs text-white/40">
            {BRAND.domain}
          </p>
        </div>
      </div>
    </footer>
  );
};
